import { useEffect, useState } from 'react';
import { PERMISSIONS_VISIBLE } from '../../config/featureFlags';
import { permissionsService } from '../../_services/permissions.service';
import { ALL_CAPS, BUILT_IN_CAPS } from './rolesStore';

/**
 * What the signed-in person can do at the active location.
 *
 * Server truth comes from GET /permissions/me (beu-node-v2 docs/permissions.md),
 * loaded on boot / sign-in and cached per person + location in localStorage
 * 'beu-access'. Until it arrives — or if it fails — we fall back to what their
 * account type has always meant (owner = everything, self-employed = Basic,
 * employed = Standard), which is exactly what the server resolves for anyone
 * nobody has given a role (permissionsCatalog.defaultRoleKey).
 *
 * These checks ALWAYS apply, on every environment (2026-10-02). The server
 * resolves and enforces user.access whatever the FE flag says, so a UI that
 * ignored it showed restricted staff buttons the server then refused.
 * PERMISSIONS_VISIBLE now only decides whether Team > Permissions (the
 * screens that CHANGE roles) is shown.
 *
 * The UI only ever HIDES things with this. The server enforces.
 */

const KEY = 'beu-access';
const listeners = new Set();

function readCache() {
    try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch (e) { return null; }
}
function activeSalonId() { return String(localStorage.getItem('salonId') || ''); }
function activeUserId() { return String(localStorage.getItem('userId') || ''); }

/** The account-type flags from the login snapshot (several historical shapes). */
export function legacyFlags() {
    let u = null;
    try { u = JSON.parse(localStorage.getItem('user')) || null; } catch (e) { u = null; }
    const hd = (u && (u.hairdresser || (u.userInfo && u.userInfo.hairdresser))) || {};
    return {
        isSalonOwner: Number(hd.isSalonOwner) === 1,
        isSelfEmployed: Number(hd.employmentTypeId) === 2 && Number(hd.isSalonOwner) !== 1,
    };
}

/**
 * The access someone with NO assigned role has, from their account type.
 * Mirrors the server's default resolution (permissionsCatalog.defaultRoleKey):
 * account holder → owner, self-employed → basic, everyone else → standard.
 */
export function defaultAccessFor(f) {
    if (f && f.isSalonOwner) return { roleKey: 'owner', roleName: 'Owner', caps: ALL_CAPS.slice(), owner: true, groupOwner: true, source: 'legacy' };
    if (f && f.isSelfEmployed) return { roleKey: 'basic', roleName: 'Basic', caps: BUILT_IN_CAPS.basic.slice(), owner: false, source: 'legacy' };
    return { roleKey: 'standard', roleName: 'Standard', caps: BUILT_IN_CAPS.standard.slice(), owner: false, source: 'legacy' };
}

function legacyAccess() {
    return defaultAccessFor(legacyFlags());
}

// Fetch /me at most once in flight; getAccess() asks for it when the cache
// doesn't match the person/location (a site switch, a sign-in path that
// didn't call loadAccess).
let _inflight = null;
let _lastKick = '';
function kick() {
    const k = `${activeUserId()}|${activeSalonId()}`;
    if (_inflight || _lastKick === k || !localStorage.getItem('accessToken')) return;
    _lastKick = k;
    loadAccess(); // eslint-disable-line no-use-before-define
}

/** Has the server's answer for this person + location landed? */
export function accessKnown() {
    const c = readCache();
    return !!(c && c.salonId && c.salonId === activeSalonId() && (!c.userId || c.userId === activeUserId()) && Array.isArray(c.caps));
}

/** Current access for the active location. */
export function getAccess() {
    if (accessKnown()) return readCache();
    try { kick(); } catch (e) { /* ignore */ }
    return legacyAccess();
}

/** Can the signed-in person do `cap` here? */
export function can(cap) {
    const a = getAccess();
    return !!(a.owner || (a.caps || []).includes(cap));
}

/** The account holder / owner at this location (never a role). */
export function isOwnerHere() { return !!getAccess().owner; }

/**
 * Owner of the WHOLE business (group owner, or a salon-on-its-own's account
 * holder). Group-wide screens (Locations, add a location) need this; a linked
 * site's own account holder is owner of their site only. Older servers don't
 * send groupOwner: treat their owner as the group owner, as before.
 */
export function isGroupOwnerHere() {
    const a = getAccess();
    if (!a.owner) return false;
    return a.groupOwner === undefined ? true : !!a.groupOwner;
}

/** Kept for older call sites: the capability, whatever the second argument. */
export function gate(cap) {
    return can(cap);
}

/**
 * Money on a sale: everyone may check out and discount their OWN bookings
 * (Charlie 2026-09-23); `pos` / `discounts` cover other people's. A sale with
 * no stylist on it (a walk-in product sale) counts as your own.
 *
 * The server enforces `pos` on taking payment (access audit 2026-10-02), with
 * the same exception as here: someone NOBODY gave a role (source 'default' /
 * 'legacy' — every salon that never opened Permissions) keeps today's open
 * checkout. Once a role is assigned, it follows the role.
 */
export function canOnSale(cap, staffIds) {
    if (can(cap)) return true;
    // No role chosen yet: today's open checkout, flag on or off — the server's
    // canTakePaymentForOthers applies the same rule (access audit 2026-10-02).
    if (isDefaultRole()) return true;
    const me = String(localStorage.getItem('userId') || '');
    const ids = (staffIds || []).filter(Boolean).map(String);
    return ids.every((id) => id === me);
}

/** Nobody chose a role for this person here: they have their account type's default. */
export function isDefaultRole() {
    const s = getAccess().source;
    return !s || s === 'default' || s === 'legacy';
}

/**
 * A part of the salon's menu: the owner, or the role's own cap for it
 * ('products' for products and stock, 'gift_cards', 'services'). No cap =
 * any of them (`catalogue`, which the server derives).
 */
export function canCatalogue(isOwner, cap) { return !!isOwner || can(cap || 'catalogue'); }

/**
 * Money actions the SERVER now gates on capabilities whatever the flag says
 * (access audit 2026-10-02). These follow can() with the flag on or off —
 * flag off, can() falls back to the account type's defaults, which are the
 * server's defaults too — so the UI never offers what the server refuses.
 */
function _me() { return String(localStorage.getItem('userId') || ''); }
function _isOwnerNow() { return !!getAccess().owner; }

/** Refund a booking's deposit: an SE's deposit only that SE; a salon deposit needs `refunds`. */
export function canRefundDeposit({ hairdresserId, sellerIsSelfEmployed } = {}) {
    if (sellerIsSelfEmployed) return !!hairdresserId && String(hairdresserId) === _me();
    return can('refunds');
}

/** Charge a no-show fee: own booking; an SE's booking that SE or the owner; else `pos`. */
export function canChargeNoShow({ hairdresserId, sellerIsSelfEmployed } = {}) {
    if (hairdresserId && String(hairdresserId) === _me()) return true;
    if (sellerIsSelfEmployed) return _isOwnerNow();
    return can('pos') || isDefaultRole();
}

/** Open a sale's refunds: an SE's sale is the SE's, the owner's or a `refunds` holder's (not `pos`). */
export function canSeeSaleRefunds({ hairdresserId, sellerIsSelfEmployed } = {}) {
    if (!sellerIsSelfEmployed) return true; // salon sale: the server decides (refunds / reports / pos)
    return (!!hairdresserId && String(hairdresserId) === _me()) || _isOwnerNow() || can('refunds');
}

/** True for a self-employed stylist working in their own book (never a role). */
// Self-employed at the active site: what the browser's login copy says, or what
// the server says (access.selfEmployed). The server's answer covers people whose
// login copy came from an older login without the hairdresser record
// (2026-10-04: SEs saw "You don't have access" on Payouts).
export function isSelfEmployedMe() { return legacyFlags().isSelfEmployed || !!(getAccess() || {}).selfEmployed; }

/**
 * Marketing (campaigns, journeys, loyalty, social, the credit wallet): owner
 * only for now (docs/permissions.md §8) — or a self-employed stylist in their
 * own book. `marketing` is stripped from every role server-side, so can()
 * only answers true for the owner today; it is checked so the day it becomes
 * grantable the UI follows.
 */
export function canMarket() { return !!getAccess().owner || isSelfEmployedMe() || can('marketing'); }

/**
 * Importing a file (clients, services, products, team): the owner, `settings`,
 * or a self-employed stylist into their own book — the server's rule
 * (importFacade). Each upload also runs a paid AI extraction.
 */
export function canImport() { return !!getAccess().owner || can('settings') || isSelfEmployedMe(); }

function notify() { listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } }); }

/** Fetch /permissions/me and cache it. Silent on failure (fallback stays). */
export async function loadAccess() {
    if (!localStorage.getItem('accessToken')) return null;
    if (_inflight) return _inflight;
    _inflight = (async () => {
    try {
        const me = await permissionsService.me();
        if (!me) return null;
        let next = { salonId: String(me.salonId || activeSalonId()), userId: activeUserId(), roleKey: me.roleKey, roleName: me.roleName, caps: me.caps || [], owner: !!me.owner, groupOwner: me.groupOwner, source: me.source, ownerOnly: me.ownerOnly || [], selfEmployed: !!me.selfEmployed };
        // Kill switch on the server (PERMISSIONS_ENFORCED=false): it answers with
        // the pre-roles rules, i.e. the account type's defaults. Show those.
        if (me.enforced === false && !me.owner) {
            const d = defaultAccessFor(legacyFlags());
            next = { ...next, caps: d.owner ? next.caps : d.caps, source: 'default' };
        }
        localStorage.setItem(KEY, JSON.stringify(next));
        notify();
        return next;
    } catch (e) {
        return null;
    } finally {
        _inflight = null;
    }
    })();
    return _inflight;
}

export function clearAccess() { try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ } notify(); }

export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Another tab signing in / switching location.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => { if (e.key === KEY || e.key === 'salonId') notify(); });
}

/** React hook: re-renders when access changes. Returns { access, can }. */
export function useAccess() {
    const [, setTick] = useState(0);
    useEffect(() => subscribe(() => setTick((t) => t + 1)), []);
    return { access: getAccess(), can };
}
